import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId) {
    // Try up to 3 times in case of timing issues
    for (let i = 0; i < 3; i++) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (data) {
        setProfile(data)
        setLoading(false)
        return
      }

      if (error && error.code !== 'PGRST116') {
        // Real error, not just "no rows"
        console.error('Profile fetch error:', error)
        setLoading(false)
        return
      }

      // Wait 500ms and retry
      await new Promise(r => setTimeout(r, 500))
    }
    setLoading(false)
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signUp(email, password, meta) {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) return { error }

    if (data.user) {
      // Wait briefly for auth to settle before inserting profile
      await new Promise(r => setTimeout(r, 300))

      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        email,
        full_name: meta.fullName,
        company_name: meta.companyName,
        account_type: meta.accountType || 'bucket',
        bucket_hours_total: parseFloat(meta.bucketHoursTotal) || 0,
        bucket_hours_used: 0,
        is_admin: false,
      })
      if (profileError) return { error: profileError }
    }
    return { error: null }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signUp, signOut, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
